import React, { useState } from 'react';
import { Button, Input, Select } from '../../../base';
import { Icon } from '../../../base/Icon';
import { Textarea } from '../../../base/Textarea';
import { HitlField } from '../../../../libs/models/agui/HitlContent';

interface HitlInputCardProps {
  fields: HitlField[];
  confirmLabel?: string;
  onSubmit?: (values: Record<string, string>) => void;
  /** read-only 모드 — 응답 완료된 카드 */
  readOnly?: boolean;
  /** read-only 시 채워서 표시할 값 */
  initialValues?: Record<string, string>;
  /** 기본 접힘 (read-only에서만 의미 있음) */
  collapsed?: boolean;
}

/** 채팅 메시지 안에 인라인으로 들어가는 HITL 입력 카드 */
export const HitlInputCard: React.FC<HitlInputCardProps> = ({
  fields,
  confirmLabel = '대화 진행하기',
  onSubmit,
  readOnly = false,
  initialValues,
  collapsed = false,
}) => {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of fields) init[f.name] = initialValues?.[f.name] ?? f.defaultValue ?? '';
    return init;
  });
  const [isOpen, setIsOpen] = useState(!collapsed);
  const [submitted, setSubmitted] = useState(false);

  const hasEmpty = fields.some((f) => f.required && !values[f.name]?.trim());
  const disabled = readOnly || submitted;

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (hasEmpty || disabled) return;
    setSubmitted(true);
    onSubmit?.(values);
  };

  const renderField = (field: HitlField, index: number) => {
    const labelText = `${field.label}${field.required ? ' *' : ''}`;
    return (
      <div key={index} className="hitl-field">
        <label className="hitl-field-label">{labelText}</label>
        {field.type === 'select' ? (
          <Select
            full
            size="large"
            value={values[field.name] ?? ''}
            disabled={disabled}
            options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
            onChange={(v: string | number) => {
              handleChange(field.name, String(v));
            }}
          />
        ) : field.type === 'textarea' ? (
          <Textarea
            value={values[field.name] ?? ''}
            rows={3}
            disabled={disabled}
            placeholder={field.defaultValue ?? `${field.label}을(를) 입력하세요`}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              handleChange(field.name, e.target.value);
            }}
          />
        ) : (
          <Input
            full
            size="large"
            value={values[field.name] ?? ''}
            maxLength={field.maxLength}
            disabled={disabled}
            placeholder={field.defaultValue ?? `${field.label}을(를) 입력하세요`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleChange(field.name, e.target.value);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="msg-content">
      <div className={`hitl-input-card ${readOnly ? 'is-readonly' : ''}`}>
        <button
          type="button"
          className="hitl-input-toggle"
          onClick={() => {
            setIsOpen((v) => !v);
          }}
        >
          <span className="hitl-input-toggle-label">
            <Icon name="check" /> 추가 입력
          </span>
          <span className={`hitl-input-toggle-chevron ${isOpen ? 'is-open' : ''}`}>
            <Icon name="chevronBottom" />
          </span>
        </button>

        {isOpen && (
          <div className="hitl-input-body">
            {fields.map(renderField)}
            {!readOnly && (
              <div className="hitl-input-actions">
                <Button size="large" variant="primary" onClick={handleSubmit} disabled={hasEmpty || disabled}>
                  {confirmLabel}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
